import type { ReactNode } from 'react';

import { Button } from './button';
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, PlusIcon } from './icons';

interface IRowListProps<T> {
  items: readonly T[];
  onChange: (next: T[]) => void;
  renderRow: (item: T, index: number) => ReactNode;
  addLabel: string;
  onAdd: () => T;
  minItems?: number;
  maxItems?: number;
}

const RowButton = ({
  label,
  disabled,
  onClick,
  danger,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  danger?: boolean;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className={`flex size-5 items-center justify-center rounded-item text-faint hover:text-primary disabled:opacity-30 ${danger ? 'hover:text-danger' : ''}`}
  >
    {children}
  </button>
);

// A list of rows the user orders by hand: add appends, remove drops, the chevrons swap a row
// with its neighbour. The step form's row fields and a prompt's messages share it.
export const RowList = <T,>({
  items,
  onChange,
  renderRow,
  addLabel,
  onAdd,
  minItems = 0,
  maxItems,
}: IRowListProps<T>) => {
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = items.slice();
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => (
        <div key={index} className="flex min-w-0 items-start gap-label">
          <div className="min-w-0 flex-1">{renderRow(item, index)}</div>
          <div className="flex shrink-0 items-center gap-tight pt-tight">
            <RowButton
              label="Move up"
              disabled={index === 0}
              onClick={() => move(index, index - 1)}
            >
              <ChevronUpIcon size={12} />
            </RowButton>
            <RowButton
              label="Move down"
              disabled={index === items.length - 1}
              onClick={() => move(index, index + 1)}
            >
              <ChevronDownIcon size={12} />
            </RowButton>
            <RowButton
              label="Remove"
              danger
              disabled={items.length <= minItems}
              onClick={() => onChange(items.filter((_, i) => i !== index))}
            >
              <DeleteIcon size={12} />
            </RowButton>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="self-start"
        disabled={maxItems !== undefined && items.length >= maxItems}
        onClick={() => onChange([...items, onAdd()])}
      >
        <PlusIcon size={12} />
        {addLabel}
      </Button>
    </div>
  );
};
